import type { ExperimentClient, Variant } from '@amplitude/experiment-react-native-client';
import { logDebug, logError, logWarning } from './logger';
import { AMPLITUDE_API_KEY, AMPLITUDE_DEPLOYMENT_KEY, INIT_AMPLITUDE_DEFAULT } from '@env';
import isMobile from './utils/isMobile';
import { PRODUCT_FAMILY_CODES } from 'src/constants/code.constants';
import { IRootStore } from 'src/store/store';
import { AnalyticsEvent } from './analyticsEvents';
import { parseAnalyticsSectionName } from './analytics.utils';
import { getQueryParam } from 'src/helpers/url.helper';
import { LOCAL_VARIANTS } from './featureFlag';
import isProduction from './utils/isProduction';
import isDev from './utils/isDev';

type AmplitudeSdk = typeof import('@amplitude/analytics-react-native');

class Amplitude {
  store: IRootStore | undefined;
  experimentClient: ExperimentClient | undefined;
  isAmplitudeInitialized: boolean = false;
  private _sdk: AmplitudeSdk | undefined;

  /**
   * The store can be the initialStore when amplitude.init() is called in parallel to initStore().
   * In that case we initialize Amplitude without user id; user identification will happen later on setAmplitudeStore.
   */
  public async init(store: IRootStore) {
    this.store = store;

    const userId = store?.user?.id ?? undefined;

    const [sdkModule, { sessionReplayPlugin }, { Experiment }] = await Promise.all([
      import('@amplitude/analytics-react-native'),
      import('@amplitude/plugin-session-replay-browser'),
      import('@amplitude/experiment-react-native-client'),
    ]);

    this._sdk = sdkModule;

    const sessionReplayTracking = sessionReplayPlugin({
      forceSessionTracking: true,
      sampleRate: 0.2,
      privacyConfig: {
        blockSelector: ['[data-testid="amp-block"]'],
        defaultMaskLevel: 'medium',
        maskSelector: ['[data-testid="amp-mask"]'],
      },
      ...((isProduction() || isDev()) && { configServerUrl: `${window.location.origin}/prx/amp-cfg` }),
    });
    sdkModule.add(sessionReplayTracking);

    return sdkModule
      .init(AMPLITUDE_API_KEY, userId, {
        ...(isProduction() && { serverUrl: `${window.location.origin}/prx/amp/2/httpapi` }),
        defaultTracking: false,
        trackingOptions: { ipAddress: false },
      } as any)
      .promise.then(async () => {
        // Initialize experiments client
        this.experimentClient = Experiment.initializeWithAmplitudeAnalytics(AMPLITUDE_DEPLOYMENT_KEY, {
          ...(isProduction() && { serverUrl: `${window.location.origin}/prx/amp-exp` }),
          fetchOnStart: true,
          // Questo await è nel percorso di boot (initializeSDKs → handleReadyOnce):
          // col default della lib (10s) una rete lenta verso /prx/amp-exp teneva
          // fermo il bootstrap per l'intera finestra (Sentry PHOTOSI-STORE-HUB-1V7,
          // ~15 utenti/giorno al cap). 5s limita il caso peggiore; chi scade resta
          // sulle variant di default, già gestite ovunque via getVariant.
          fetchTimeoutMillis: 5000,
          // Lo start() qui sotto è già .catch-ato: le unhandled rejection su Sentry
          // arrivavano dalla catena di RETRY IN BACKGROUND interna alla lib, non
          // catturabile da qui. Senza retry una sessione che ha mancato il fetch
          // tiene le default (stessa UX) e il rumore sparisce alla fonte.
          retryFetchOnFailure: false,
        });
        await this.experimentClient.start().catch(e => {
          logWarning('[AMP] - experiment client fetch failed, variants unavailable', e);
        });
        if (__DEV__) {
          logDebug('[AMP] - experiment client initialized (DEV MODE)', new Date().toISOString(), LOCAL_VARIANTS);
        } else {
          logDebug(
            '[AMP] - experiment client initialized (PROD MODE)',
            new Date().toISOString(),
            this.experimentClient.all(),
          );
        }

        this.isAmplitudeInitialized = true;

        await this.trackUserAndSyncSDK();
      })
      .catch(e => {
        logError('[AMP] - client failed to initialize', e);
      });
  }

  protected async sendEvent(name: string, data: Record<string, any> = {}): Promise<void> {
    let sendEventPromise: Promise<void> | undefined;
    if (__DEV__) {
      logDebug(`[AMP] - MOCKED send event: ${name} | Data: `, data, ` | Time: `, new Date().toISOString());
    } else if (this.isAmplitudeInitialized && this._sdk) {
      sendEventPromise = this._sdk
        .logEvent(name, data)
        .promise.then(result => {
          logDebug(`[AMP] - send event: ${name} | Data: `, data, ` | Time: `, new Date().toISOString(), result);
        })
        .catch(e => {
          logError('[AMP] - event failed to send', e);
        });
    }

    return sendEventPromise ?? Promise.resolve();
  }

  public setAmplitudeFeatureFlag(featureFlag: string, featureFlagValue: string): void {
    if (!this._sdk) return;
    const identify = new this._sdk.Identify();
    identify.set(featureFlag, featureFlagValue);
    this._sdk.identify(identify);
  }

  public async setUserInfo(userId: string | null, userType: string): Promise<void> {
    if (this.isAmplitudeInitialized && this._sdk) {
      this._sdk.setUserId(!!userId ? userId : undefined);
    }
    return void 0;
  }

  private getVariant(key: string): Variant | undefined {
    let variant = undefined;
    if (__DEV__) {
      variant = LOCAL_VARIANTS[key];
    } else if (this.experimentClient) {
      variant = this.experimentClient.variant(key);
    } else {
      logWarning('[AMP] - experimentClient not initialized, cannot get variant for key:', key);
    }

    return variant;
  }

  public async trackEvent({ name }: AnalyticsEvent, data: Record<string, any> = {}): Promise<void> {
    if (typeof this.store !== 'undefined') {
      const {
        project: { currentRoute, activeGroup, groupCode },
        dynamicModal: { activeModal },
      } = this.store;
      const { product_group, section_name, ...restData } = data;
      const text_id = (product_group ?? activeGroup?.code ?? groupCode ?? PRODUCT_FAMILY_CODES.HOMEDECOR).toUpperCase();

      await this.sendEvent(name, {
        text_id,
        product_group: text_id,
        section_name: section_name ?? parseAnalyticsSectionName(activeModal?.dynamicModalView ?? currentRoute), // page
        ...restData,
      });
    }
  }

  public canBeInitialized() {
    let canBeInit = JSON.parse(INIT_AMPLITUDE_DEFAULT) || false;
    if (!canBeInit) {
      const queryValue = getQueryParam('amplitude');
      canBeInit = !queryValue || queryValue === 'true';
    }
    return canBeInit;
  }

  public setAmplitudeStore(store: IRootStore) {
    this.store = store;

    if (this.isAmplitudeInitialized) {
      this.trackUserAndSyncSDK();
    }
  }

  public async trackUserAndSyncSDK(): Promise<void> {
    if (!!this.store?.isStoreReady) {
      // If `Amplitude.init()` was already executed (in parallel with initStore in React),
      // at that point:
      //   - `identifyUserIfAvailable` had early return because store it wasn't ready
      //   - `syncSDKFlagVariants` had silent skip for the same reason
      // Now that we receive the real store (with user and SDK ready) we re-execute
      // entrambi. Fire-and-forget perché chiamato da useEffect.
      await this.identifyUserIfAvailable().catch(e => logError('[AMP] - identify after setAmplitudeStore failed', e));
      await this.syncSDKFlagVariants().catch(e => logError('[AMP] - flag re-sync after setAmplitudeStore failed', e));
    }
  }

  private async identifyUserIfAvailable() {
    if (!this._sdk) return;
    try {
      const { id: userId, userType = '' } = this.store?.user || {};
      // Set user ID
      if (userId) {
        this._sdk.setUserId(userId);
      }

      // Set user properties
      const platform: string = isMobile() ? 'Mobile' : 'Desktop';
      const identifyObj = new this._sdk.Identify();
      identifyObj.set('platform_type', platform);
      identifyObj.set('user_type', userType);
      await this._sdk.identify(identifyObj).promise;

      logDebug('[AMP] - user identified', { userId, userType });
    } catch (e) {
      logError('[AMP] - identifyUserIfAvailable failed', e);
    }
  }

  private async syncSDKFlagVariants() {
    try {
      if (!this.experimentClient) {
        logWarning('[AMP] - experimentClient not initialized, cannot sync SDK flag variants');
      } else if (!this.store?.user?.sdk) {
        // The store here can be the initialStore if init() was called in parallel to the Root store initialization (see Didomi/index.tsx).
        // In that case store.user.sdk is undefined: we do a silent skip
        // The sync will be re-executed by setAmplitudeStore when the real store arrives (which has the SDK ready).
        logDebug(
          '[AMP] - SDK not yet ready, deferring flag sync (will retry on setAmplitudeStore)',
          new Date().toISOString(),
        );
      } else {
        await this.store.user.sdk.setFlagVariants(this.getVariantValue.bind(this));
        logDebug('[AMP] - SDK flag variants synced', new Date().toISOString());
      }
    } catch (e) {
      logError('[AMP] - SDK flag variants sync failed', e);
    }
  }

  /**
   * Retrieves the variant value for a given experiment flag key.
   *
   * @param key - The experiment flag key to retrieve the variant for
   * @returns The variant value (string or boolean) if available (depending on amplitude settings), undefined otherwise
   *
   * @example
   * const variant = Amplitude.getVariant('my-experiment-flag');
   * if (variant === 'treatment') {
   *   // Show new feature
   * } else {
   *   // Show control
   * }
   */
  public getVariantValue(key: string): string | boolean | undefined {
    let parsedVariantValue: string | boolean | undefined = undefined;
    try {
      let variantValue = this.getVariant(key)?.value;

      parsedVariantValue =
        variantValue === 'true' || variantValue === 'false' ? JSON.parse(variantValue) : variantValue;
    } catch (e) {
      logError('[AMP] - failed to get variant for key:', key, e);
    }
    return parsedVariantValue;
  }
}

export default new Amplitude();
